/**
 * useUiStore — pure view state for the GRIDBOX redesign (HANDOFF §6).
 *
 * This holds only presentation state (theme, range, search, view mode,
 * filters, report metric). Domain data continues to come from the existing
 * `@fermata/grpc-zustand` service stores — do not duplicate it here.
 *
 * Component-local state (modal open, active tab, dropdown open) belongs in
 * `useState` / Radix's own controlled state, not in this global store.
 *
 * Theme follows the OS preference until the user makes an explicit choice
 * (`themeSource: "user"`), after which the choice persists to localStorage.
 * `useTheme` applies the resolved theme to <html data-theme> and keeps it in
 * sync with OS changes while still on "system".
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { UserPreferences } from "../types/user-preferences"

export type Theme = "light" | "dark"
export type ThemeSource = "system" | "user"
export type Range = "day" | "week" | "month" | "year"
export type ListView = "list" | "grid"
export type ReportMetric = "revenue" | "energy" | "peak" | "co2"

export interface UiState {
  theme: Theme
  themeSource: ThemeSource
  range: Range
  search: string
  listView: ListView
  filterStatus: string
  scopeSites: string[] // [] = all sites
  reportMetric: ReportMetric
  /**
   * Custom report window (Uptime / Sessions / Carbon filter bar): when set it
   * replaces the Day / Week / Month / Year window. Not persisted.
   */
  reportWindow: { startMs: number; endMs: number } | null

  // actions
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  /** Revert to OS-following mode; recomputes the theme from the OS. */
  resetToSystem: () => void
  /** Internal: update theme from an OS change without flipping to "user". */
  setSystemTheme: (theme: Theme) => void
  /** Apply server-persisted preferences (server wins over localStorage). */
  applyServerPreferences: (prefs: UserPreferences) => void
  setRange: (range: Range) => void
  setSearch: (search: string) => void
  setListView: (listView: ListView) => void
  setFilterStatus: (filterStatus: string) => void
  setScope: (scopeSites: string[]) => void
  setReportMetric: (reportMetric: ReportMetric) => void
  setReportWindow: (reportWindow: { startMs: number; endMs: number } | null) => void
}

/**
 * Resolve the OS-preferred theme. Defaults to dark when the preference is
 * undeterminable (SSR / no `matchMedia`) — the GRIDBOX design is dark-first —
 * while still following an explicit OS preference. Mirrors the before-paint
 * fallback in index.html. Exported for tests.
 */
export function systemTheme(): Theme {
  if (typeof window === "undefined" || !window.matchMedia) return "dark"
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

/**
 * What gets persisted to localStorage. The theme *source* is always saved, but
 * the resolved `theme` only when the user explicitly chose it — on "system" we
 * omit it so a fresh load recomputes from the OS (prevents a stale-theme flash).
 */
export function persistedUiState(s: UiState): Partial<UiState> {
  return s.themeSource === "user"
    ? { theme: s.theme, themeSource: s.themeSource }
    : { themeSource: s.themeSource }
}

/** Map store state to the server theme preference value. */
export function themePreferenceFromState(
  theme: Theme,
  themeSource: ThemeSource
): NonNullable<UserPreferences["theme"]> {
  return themeSource === "system" ? "system" : theme
}

export function applyThemePreference(
  prefs: UserPreferences
): Pick<UiState, "theme" | "themeSource"> | null {
  if (!prefs.theme) return null
  if (prefs.theme === "system") {
    return { themeSource: "system", theme: systemTheme() }
  }
  return { themeSource: "user", theme: prefs.theme }
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: systemTheme(),
      themeSource: "system",
      range: "week",
      search: "",
      listView: "list",
      filterStatus: "all",
      scopeSites: [],
      reportMetric: "revenue",
      reportWindow: null,

      setTheme: (theme) => set({ theme, themeSource: "user" }),
      toggleTheme: () =>
        set((s) => ({ theme: s.theme === "dark" ? "light" : "dark", themeSource: "user" })),
      resetToSystem: () => set({ theme: systemTheme(), themeSource: "system" }),
      setSystemTheme: (theme) => set((s) => (s.themeSource === "system" ? { theme } : {})),
      applyServerPreferences: (prefs) => {
        const next = applyThemePreference(prefs)
        if (next) set(next)
      },
      setRange: (range) => set({ range }),
      setSearch: (search) => set({ search }),
      setListView: (listView) => set({ listView }),
      setFilterStatus: (filterStatus) => set({ filterStatus }),
      setScope: (scopeSites) => set({ scopeSites }),
      setReportMetric: (reportMetric) => set({ reportMetric }),
      setReportWindow: (reportWindow) => set({ reportWindow }),
    }),
    {
      name: "ui-store",
      // See persistedUiState: only persist the resolved theme for explicit user
      // choices so system-followers recompute from the OS on each load.
      partialize: persistedUiState,
    }
  )
)
